<template>
  <v-dialog persistent v-model="showAssignLicenceUserDialog" width="500">
    <div class="ec-dialog">
      <div class="ec-dialog__header">
        <h2 class="ec-dialog__title">Assign Licence</h2>
        <button class="ec-icon-btn" @click="closeCustomLicenceDialog">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <v-form lazy-validation v-model="form" ref="assignLicenceForm" :disable="loading">
        <div class="ec-dialog__body">
          <div class="ec-field">
            <label class="ec-label">Licence *</label>
            <v-combobox
              v-model="selectLicenceKey"
              :rules="[rules.required]"
              :items="licenceKeys"
              dense outlined hide-details="auto"
              return-object
              item-text="licence_name"
              single-line
              item-value="id"
              placeholder="Select licence…"
              @change="fetchPoolInfo"
            />
            <div v-if="poolInfo !== null" style="margin-top:6px;font-size:12px;" :style="{ color: poolInfo.available_slots > 0 ? 'var(--ec-success)' : 'var(--ec-danger)' }">
              <v-icon size="13" :color="poolInfo.available_slots > 0 ? 'success' : 'error'">{{ poolInfo.available_slots > 0 ? 'mdi-database-check' : 'mdi-database-remove' }}</v-icon>
              {{ poolInfo.available_slots }} slot available ({{ poolInfo.assigned_slots }} / {{ poolInfo.total_slots }} assigned)
            </div>
            <div v-if="poolInfo !== null && quantity > poolInfo.available_slots" style="margin-top:4px;font-size:12px;color:var(--ec-danger);">
              <v-icon size="13" color="error">mdi-alert-circle-outline</v-icon>
              Quantity exceeds available slots (max {{ poolInfo.available_slots }})
            </div>
            <div v-if="selectLicenceKey && poolInfo === null && !poolLoading" style="margin-top:6px;font-size:12px;color:#9ca3af;">
              <v-icon size="13" color="#9ca3af">mdi-infinity</v-icon>
              No pool restriction (SaaS mode)
            </div>
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
            <div class="ec-field">
              <label class="ec-label">Quantity *</label>
              <v-text-field
                v-model.number="quantity"
                :rules="[rules.required, rules.mustMoreThanZero]"
                dense outlined hide-details="auto"
                type="number" class="inputPrice"
              />
            </div>
            <div class="ec-field">
              <label class="ec-label">Date Type *</label>
              <v-select
                v-model="selectedDateType"
                :items="dateTypes"
                :rules="[rules.required]"
                dense outlined hide-details="auto"
                placeholder="Select type…"
              />
            </div>
          </div>

          <div v-if="selectedDateType === 'custom'" style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
            <div class="ec-field">
              <label class="ec-label">Custom Date</label>
              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                :return-value.sync="getEndDate"
                transition="scale-transition"
                offset-y min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="getEndDate"
                    dense outlined hide-details="auto"
                    append-icon="mdi-calendar"
                    v-bind="attrs" v-on="on"
                    placeholder="MM/DD/YYYY"
                  />
                </template>
                <v-date-picker :allowed-dates="disablePastDates" v-model="getEndDate" no-title scrollable>
                  <v-spacer />
                  <v-btn text color="primary" @click="$refs.menu.save(getEndDate)">OK</v-btn>
                </v-date-picker>
              </v-menu>
            </div>
            <div class="ec-field">
              <label class="ec-label">Duration (days) *</label>
              <v-text-field
                v-model.number="duration"
                :rules="[rules.required, rules.mustMoreThanZero]"
                dense outlined hide-details="auto"
                type="number" class="inputPrice"
              />
            </div>
          </div>
        </div>

        <div class="ec-dialog__footer">
          <button class="ec-btn ec-btn--ghost" type="button" @click="closeCustomLicenceDialog">Cancel</button>
          <button class="ec-btn ec-btn--primary" type="button"
                  :disabled="!form || loadingAssignLicence || (poolInfo !== null && quantity > poolInfo.available_slots)" @click="createNewLicence()">
            <v-progress-circular v-if="loadingAssignLicence" size="13" width="2" indeterminate style="margin-right:6px" />
            Assign
          </button>
        </div>
      </v-form>
    </div>
  </v-dialog>
</template>

<script>
import converter from '@/mixins/converter'
import Swal from 'sweetalert2'
import { apiService } from '../../service/api.service'

export default converter.extend({
  props: {
    showAssignLicenceUserDialog: { type: Boolean, default: false },
    licenceKeys: { type: Array, default: () => [] },
    selectedUser: { type: Object, required: true }
  },
  watch: {
    getEndDate: {
      handler (n) {
        this.watchGetEndDate(n)
      },
      deep: true
    }
  },
  data: () => ({
    editDialog: false,
    loading: false,
    form: true,
    duration: 0,
    quantity: 1,
    showCustomLicenceDialog: false,
    loadingAssignLicence: false,
    newLicencePayload: {},
    selectLicenceKey: null,
    poolInfo: null,
    poolLoading: false,
    getEndDate: null,
    menu: false,
    featuresPayload: [],
    features: [],
    addressPayload: {},
    rules: {
      length: (len) => (v) =>
        (v || '').length <= len || `Name length can max ${len}.`,
      required: (v) => !!v || 'This field is required',
      numberRule: (v) => {
        if (!isNaN(parseFloat(v)) && v >= 0) return true
        return 'Number has to be between 0 and 9'
      },
      mustMoreThanZero: (v) => {
        if (!isNaN(v) && v > 0) return true
        return 'Number must be more than 0'
      }
    },
    select: ['Vuetify'],
    items: ['Programming', 'Design', 'Vue', 'Vuetify'],
    selectedDateType: '',
    dateTypes: ['monthly', 'yearly', 'custom']
  }),

  methods: {
    async createNewLicence () {
      if (this.$refs.assignLicenceForm.validate()) {
        this.loadingAssignLicence = true
        this.form = false
        const assignLicencePayload = {
          licence_id: this.selectLicenceKey.id,
          user_id: this.selectedUser.id,
          date_type: this.selectedDateType,
          quantity: this.quantity,
          duration: this.duration
        }
        await apiService
          .post('admin-assign-licence-to-user', assignLicencePayload)
          .then(() => {
            Swal.fire(
              'Assigned!',
              'Licence is assigned to user successfully!',
              'success'
            )
            this.loadingAssignLicence = false
            this.form = true
            this.closeCustomLicenceDialog()
            this.$emit('callSyncUserLicences')
          })
          .catch((err) => {
            const msg = err?.response?.data?.message || 'An error occurred while assigning the licence.'
            Swal.fire('Error!', msg, 'error')
            this.loadingAssignLicence = false
            this.form = true
          })
      }
    },

    async fetchPoolInfo (licence) {
      this.poolInfo = null
      if (!licence || !licence.id) return
      this.poolLoading = true
      try {
        const { data } = await apiService.get('product-key/status')
        if (data.mode === 'onpremise') {
          const pool = (data.pools || []).find(p => p.licence_type_id === licence.id)
          this.poolInfo = pool || { total_slots: 0, assigned_slots: 0, available_slots: 0 }
        }
      } catch {
        this.poolInfo = null
      } finally {
        this.poolLoading = false
      }
    },

    watchGetEndDate (date) {
      const convertedDate = new Date(date)
      const durationDay = this.calculateExpireTime(convertedDate.getTime())
      this.duration = durationDay
    },

    disablePastDates (val) {
      return val >= new Date().toISOString().substr(0, 10)
    },

    closeCustomLicenceDialog () {
      this.poolInfo = null
      this.$emit('closeShowAssignLicenceDialog')
      this.$refs.assignLicenceForm.reset()
    },

    openCustomerLicenceDialog () {
      this.showCustomLicenceDialog = true
    }
  }
})
</script>

<style>
.inputPrice input[type='number'] {
  -moz-appearance: textfield;
}
.inputPrice input::-webkit-outer-spin-button,
.inputPrice input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
</style>
